import { defineStore } from "pinia";
import { useBasketStore } from "./BasketStore";
import { computed, ComputedRef, Ref, ref } from "vue";
import { useGA4 } from "../composables/GA4";
import { useCustomerStore } from "./CustomerStore";
import ApiService from "../composables/ApiService";
import CheckoutCustomizationEventBus from "../EventBus/CheckoutCustomizationEventBus";
import BeforeDeliveryStepContinue from "../events/Checkout/BeforeDeliveryStepContinue";
import AfterOrderPay from "../events/Checkout/AfterOrderPay";
import AfterCheckoutError from "../events/Checkout/AfterCheckoutError";
import { collect } from "collect.js";
import { Basket, Order, PaymentMethod, PickupPoint, ShippingMethod } from "@daalder/types";
import Cookies from "js-cookie";
import DayJs from "../composables/DayJs";
import mitt, { Emitter } from "mitt";

export enum NextStepStates {
    NORMAL = "NORMAL",
    DISABLED = "DISABLED",
    LOADING = "LOADING",
}

export enum CheckoutStepUrls {
    BASKET = "/cart",
    CUSTOMERINFO = "/checkout/client-form",
    DELIVERY = "/checkout/delivery",
    PAYMENT = "/checkout/payments",
    SUCCESS = "/checkout/success",
}

export enum CheckoutSteps {
    BASKET = "BASKET",
    CUSTOMERINFO = "CUSTOMERINFO",
    DELIVERY = "DELIVERY",
    PAYMENT = "PAYMENT",
    SUCCESS = "SUCCESS",
}

export const CheckoutStoreEventBus: Emitter<{
    afterNextStep: void;
}> = mitt<{
    afterNextStep: void;
}>();

export const useCheckoutStore = defineStore("checkout", () => {
    // Data
    let apiService: ApiService = new ApiService();
    const minOrderPrice: Ref<number> = ref(+(window.daalder_basket_min_order_price ?? 0));
    const minPickupPrice: Ref<number> = ref(+(window.daalder_basket_pickup_min_price ?? 0));
    const shippingMethods: Ref<ShippingMethod[]> = ref([]);
    const pickupPoints: Ref<PickupPoint[]> = ref([]);
    const paymentMethods: Ref<PaymentMethod[]> = ref([]);
    const isNextStepLoading: Ref<boolean> = ref(false);
    const formValidationMessages: Ref<any[]> = ref([]);
    const paymentMethodHasError: Ref<boolean> = ref(false);
    // This variable is used to add support for the progress-buttons component in their current form
    const requestContinueToNextStep: Ref<boolean> = ref(false);
    const redirectURLDelivery: Ref<CheckoutSteps> = ref(null);
    const isCheckoutStoreDataFetched: Ref<boolean> = ref(false);
    const areTermsAccepted: Ref<boolean> = ref(false);
    const isPickupListOpen = ref(false);
    const currentStep: Ref<CheckoutSteps> = ref(null);
    const cachedCreateOrderResponse: Ref<Order> = ref(null);
    const clientFormIsValid: Ref<boolean> = ref(true);
    /** @description This flag can be used to hard-set the checkout continue button to the DISABLED state. */
    const customOverwriteNextStepStateToDisabled: Ref<boolean> = ref(false);

    // Composables
    const {
        trackCheckoutValidationErrors,
        mapBasketEntriesToGAProductsData,
        trackGAPurchase,
        trackGAAddUserInfo,
        trackGACheckoutSummary,
    } = useGA4();

    // Pinia stores
    const basketStore = useBasketStore();
    const customerStore = useCustomerStore();

    // Computed properties
    const hasValidNoMinimumOrderValueCookie: ComputedRef<boolean> = computed(() => {
        let cookie = Cookies.get(btoa("no_minimum_order_value_until"));

        if (cookie !== null && cookie !== undefined) {
            let now = DayJs.getInstance()();
            let cookieDate = DayJs.getInstance()(cookie, "YYYY-MM-DD HH:mm:ss");

            if (cookieDate.diff(now) > 0) {
                return true;
            }
        }

        return false;
    });

    const minOrderAmountRuleIsMet: ComputedRef<boolean> = computed(() => {
        if (hasValidNoMinimumOrderValueCookie.value) {
            return true;
        }

        return +basketStore.basket?.subtotal?.price >= minOrderPrice.value;
    });

    const minPickupAmountRuleIsMet: ComputedRef<boolean> = computed(() => {
        return +basketStore.basket?.subtotal?.price >= minPickupPrice.value;
    });

    const pickupPointIsAllowed = computed(() => {
        let cookie = Cookies.get(btoa("no_minimum_order_value_for_pickup_until"));

        if (cookie !== null && cookie !== undefined) {
            let now = DayJs.getInstance()();
            let cookieDate = DayJs.getInstance()(cookie, "YYYY-MM-DD HH:mm:ss");

            if (cookieDate.diff(now) > 0) {
                return true;
            }
        }

        return minOrderAmountRuleIsMet.value && minPickupAmountRuleIsMet.value;
    });

    const selectedShippingMethod: ComputedRef<ShippingMethod | null> = computed(() => {
        return basketStore.basket?.shipping_method;
    });

    const selectedPickupPoint: ComputedRef<PickupPoint | null> = computed(() => {
        return basketStore.basket?.pickup_point;
    });

    const selectedPaymentMethod: ComputedRef<PaymentMethod | null> = computed(() => {
        return basketStore.basket?.payment_method;
    });

    // Watchers
    customerStore.$subscribe(async (mutation, state) => {
        // Null indicates that the current page is not a checkout page
        if (currentStep.value !== null) {
            if (state.customer) {
                if (!isCheckoutStoreDataFetched.value) {
                    isCheckoutStoreDataFetched.value = true;

                    await Promise.all([fetchPickupPoints(), fetchShippingMethods(), fetchPaymentMethods()]);

                    // This listener is run on initial page load, so the current step setting still has to be run.
                    loadCurrentStepFromUrl();
                } else {
                    // The customer changed after page load, most likely after the submission of the customer detail form
                    // Make sure the delivery methods and payment methods are fetched using the context of the customer's
                    // country
                    await Promise.all([fetchPickupPoints(), fetchShippingMethods(), fetchPaymentMethods()]);
                }
            }
        }
    });

    basketStore.$subscribe((mutation, state) => {
        // Null indicates that the current page is not a checkout page
        if (currentStep.value !== null) {
            if (state.basket) {
                fetchPaymentMethods();
            }
        }
    });

    // On create
    init();

    // Methods
    function init() {
        // On in-checkout navigation (no actual page load)
        addEventListener("popstate", (event) => {
            loadCurrentStepFromUrl();
        });

        loadCurrentStepFromUrl();

        // Null indicates that the current page is not a checkout page
        if (currentStep.value !== null) {
            fetchPaymentMethods().then(() => {
                if (currentStep.value === CheckoutSteps.PAYMENT) {
                    presetPaymentMethod();
                }
            });

            fetchShippingMethods().then(() => {
                if (currentStep.value === CheckoutSteps.DELIVERY) {
                    presetDeliveryMethod();
                }
            });
        }
    }

    function loadCurrentStepFromUrl() {
        // Detect the current page from the url
        if (window.location.pathname.includes(CheckoutStepUrls.CUSTOMERINFO)) {
            currentStep.value = CheckoutSteps.CUSTOMERINFO;
        } else if (window.location.pathname.includes(CheckoutStepUrls.DELIVERY)) {
            currentStep.value = CheckoutSteps.DELIVERY;
        } else if (window.location.pathname.includes(CheckoutStepUrls.PAYMENT)) {
            currentStep.value = CheckoutSteps.PAYMENT;
        } else if (window.location.pathname.includes(CheckoutStepUrls.SUCCESS)) {
            currentStep.value = CheckoutSteps.SUCCESS;
        } else {
            // The current page is not checkout
            currentStep.value = null;
        }
    }

    function navigateToStep(checkoutStep: CheckoutSteps) {
        if (checkoutStep === CheckoutSteps.BASKET) {
            window.location.href = CheckoutStepUrls[checkoutStep];
        } else {
            currentStep.value = checkoutStep;
            window.history.pushState("", "", CheckoutStepUrls[checkoutStep]);
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        }

        if (checkoutStep === CheckoutSteps.DELIVERY) {
            presetDeliveryMethod();
        }

        if (checkoutStep === CheckoutSteps.PAYMENT) {
            presetPaymentMethod();
        }
    }

    function nextStepState(): NextStepStates {
        if (customOverwriteNextStepStateToDisabled.value) {
            return NextStepStates.DISABLED;
        }

        if (!basketStore.basket?.entries?.length || !minOrderAmountRuleIsMet.value || basketStore.isCurrentlyMutating) {
            return NextStepStates.DISABLED;
        }

        if (!clientFormIsValid.value) {
            return NextStepStates.DISABLED;
        }

        if (currentStep.value === CheckoutSteps.DELIVERY) {
            if (!basketStore.basket?.shipping_method && !basketStore.basket?.pickup_point) {
                return NextStepStates.DISABLED;
            }
        }

        if (currentStep.value === CheckoutSteps.PAYMENT) {
            if (
                !basketStore.basket?.payment_method ||
                (!areTermsAccepted.value && !!window.checkout_must_confirm_terms_and_conditions)
            ) {
                return NextStepStates.DISABLED;
            }
        }

        return NextStepStates.NORMAL;
    }

    function paymentMethodMinMaxOrderValueRuleMet(paymentMethod: PaymentMethod) {
        // Basket isn't fetched yet
        if (!basketStore.basket) {
            return false;
        }

        // Order value is too small
        if (paymentMethod.min_order_value !== null && +basketStore.basket.total.price < paymentMethod.min_order_value) {
            return false;
        }

        // Order value is too large
        if (paymentMethod.max_order_value !== null && +basketStore.basket.total.price > paymentMethod.max_order_value) {
            return false;
        }

        // valid order value
        return true;
    }

    async function updateCustomer(postData: any) {
        customerStore
            .updateCustomer(postData)
            .then((response) => {
                let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
                trackGAAddUserInfo(mappedGAProducts);

                isNextStepLoading.value = false;
                navigateToStep(CheckoutSteps.DELIVERY);
            })
            .catch((error) => {
                isNextStepLoading.value = false;

                // If there are field validation errors on the response data
                if (error?.response?.data?.errors) {
                    formValidationMessages.value = error.response.data.errors;

                    // Get the error message and the fields on which validation messages were returned
                    let errorMessage = error.response.data?.message ?? error.response.statusText;
                    let fields = Object.fromEntries(Object.entries(error.response.data.errors) ?? []);

                    // Log the checkout_validation_errors event to GA4
                    let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
                    trackCheckoutValidationErrors(errorMessage, mappedGAProducts, fields);
                } else {
                    // Else, still log the error to GA4
                    let errorMessage = error.response.data?.message ?? error.response.statusText;
                    let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
                    trackCheckoutValidationErrors(errorMessage, mappedGAProducts);
                }
            });
    }

    async function registerCustomer(postData: any) {
        customerStore
            .registerCustomer(postData)
            .then((response) => {
                let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
                trackGAAddUserInfo(mappedGAProducts);

                isNextStepLoading.value = false;
                navigateToStep(CheckoutSteps.DELIVERY);
            })
            .catch((error) => {
                isNextStepLoading.value = false;

                formValidationMessages.value = error?.response?.data?.errors ?? {};

                // If there are field validation errors on the response data
                if (error?.response?.data?.errors) {
                    // Get the error message and the fields on which validation messages were returned
                    let errorMessage = error.response.data?.message ?? error.response.statusText;
                    let fields = Object.keys(error.response.data.errors) ?? [];

                    // Log the checkout_validation_errors event to GA4
                    let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
                    trackCheckoutValidationErrors(errorMessage, mappedGAProducts, fields);
                } else {
                    // Else, still log the error to GA4
                    let errorMessage = error.response.data?.message ?? error.response.statusText;
                    let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
                    trackCheckoutValidationErrors(errorMessage, mappedGAProducts);
                }
            });
    }

    async function registerOrUpdateCustomer(isPrefilled: boolean, postData: any) {
        if (isNextStepLoading.value) {
            return;
        }

        formValidationMessages.value = [];
        isNextStepLoading.value = true;

        // update existing customer
        if (isPrefilled) {
            await updateCustomer(postData);
        } else {
            // register new customer
            await registerCustomer(postData);
        }
    }

    async function fetchShippingMethods() {
        let countryCode =
            customerStore.customer?.primaryDeliveryAddress?.country_code ||
            window.wordpress_locale.substring(3, 5) ||
            Object.keys(window.allowed_checkout_shipping_countries)?.[0] ||
            "NL";

        return await apiService
            .get(`v2/basket/shipping-methods/${window.store_id}/${countryCode}`)
            .then((response) => {
                shippingMethods.value = response.data.data;
            })
            .catch((error) => {
                console.log(error);
            });
    }

    async function fetchPickupPoints() {
        return await apiService
            .get(`v2/stores/${window.store_id}/pickup-points?includes=address,weekdays`)
            .then((response) => {
                pickupPoints.value = collect(response.data.data as PickupPoint[])
                    .sortBy("sort_order")
                    .all();
            })
            .catch((error) => {
                console.log(error);
            });
    }

    async function fetchPaymentMethods() {
        return await apiService
            .get(`v2/stores/${window.store_id}/payment-methods`)
            .then((response) => {
                paymentMethods.value = response.data;
            })
            .catch((error) => {
                console.log(error);
            });
    }

    function updateShippingMethodLocal(shippingMethod: ShippingMethod) {
        if (isPickupListOpen.value) {
            isPickupListOpen.value = false;
        }

        if (basketStore.basket.pickup_point) {
            const priceWithoutPickup =
                parseFloat(basketStore.basket.total.price) - getPickupPrice(basketStore.basket?.pickup_point);
            const newTotalPrice = priceWithoutPickup + parseFloat(shippingMethod.price.price);
            basketStore.basket.total.price = newTotalPrice.toString();
        } else if (basketStore.basket.shipping_method) {
            const priceWithoutShipping =
                parseFloat(basketStore.basket.total.price) -
                parseFloat(basketStore.basket.shipping_method?.price?.price);
            const newTotalPrice = priceWithoutShipping + parseFloat(shippingMethod.price.price);
            basketStore.basket.total.price = newTotalPrice.toString();
        } else if (basketStore.basket.suggested_shipping_method) {
            const priceWithoutShipping =
                parseFloat(basketStore.basket.total.price) -
                parseFloat(basketStore.basket.suggested_shipping_method?.price?.price);
            const newTotalPrice = priceWithoutShipping + parseFloat(shippingMethod.price.price);
            basketStore.basket.total.price = newTotalPrice.toString();
        } else {
            basketStore.basket.total.price = (
                parseFloat(basketStore.basket.total.price) + parseFloat(shippingMethod?.price?.price)
            ).toString();
        }

        const vatPercentage = basketStore.basket.total.vat_rate_percentage;
        basketStore.basket.total.price_excluding_vat = calculatePriceExcludingVat(
            vatPercentage,
            +basketStore.basket.total.price,
        ).toString();

        basketStore.basket.shipping_method = shippingMethod;
        basketStore.basket.shipping_method_id = shippingMethod.id;
        basketStore.basket.pickup_point = null;
        basketStore.basket.pickup_point_id = null;
    }

    function getPickupPrice(pickupPoint: PickupPoint) {
        const hasFreePickup =
            parseFloat(basketStore.basket?.subtotal?.price ?? "0") >
            parseFloat(pickupPoint?.free_from_price?.price ?? "0");
        return hasFreePickup ? 0 : parseFloat(pickupPoint?.price?.price ?? "0") || 0;
    }

    function calculatePriceExcludingVat(vat_rate_percentage: number, price: number) {
        return price / (vat_rate_percentage / 100 + 1);
    }

    function updatePickupPointLocal(pickupPoint: PickupPoint) {
        if (pickupPointIsAllowed.value === false || !pickupPoint) {
            return false;
        }
        if (!isPickupListOpen.value) {
            isPickupListOpen.value = true;
        }

        let newTotalPrice;
        if (basketStore.basket.shipping_method) {
            const priceWithoutShipping =
                parseFloat(basketStore.basket.total.price) -
                parseFloat(basketStore.basket.shipping_method?.price?.price);
            newTotalPrice = priceWithoutShipping + getPickupPrice(pickupPoint);
        } else {
            const priceWithoutPickup =
                parseFloat(basketStore.basket.total.price) - getPickupPrice(basketStore.basket?.pickup_point);
            newTotalPrice = priceWithoutPickup + getPickupPrice(pickupPoint);
        }

        basketStore.basket.total.price = newTotalPrice.toString();
        const vatPercentage = basketStore.basket.total.vat_rate_percentage;
        basketStore.basket.total.price_excluding_vat = calculatePriceExcludingVat(
            vatPercentage,
            newTotalPrice,
        ).toString();

        basketStore.basket.pickup_point = pickupPoint;
        basketStore.basket.pickup_point_id = pickupPoint.id;
        basketStore.basket.shipping_method = null;
        basketStore.basket.shipping_method_id = null;
    }

    function updatePaymentMethodLocal(paymentMethod: PaymentMethod) {
        if (!paymentMethod) {
            return false;
        }

        const vatPercentage = basketStore.basket.total.vat_rate_percentage;
        if (basketStore.basket.payment_method) {
            const priceWithoutTransactionFee =
                parseFloat(basketStore.basket.total.price) -
                parseFloat(basketStore.basket.payment_method.transaction_fee.price);
            const newTotalPrice = priceWithoutTransactionFee + parseFloat(paymentMethod.transaction_fee.price);

            basketStore.basket.total.price_excluding_vat = calculatePriceExcludingVat(
                vatPercentage,
                newTotalPrice,
            ).toString();
            basketStore.basket.total.price = newTotalPrice.toString();
            basketStore.basket.payment_method = paymentMethod;
            basketStore.basket.payment_method_id = paymentMethod.id;
            basketStore.basket.payment_method.issuer = paymentMethod.issuer;
        } else {
            basketStore.basket.total.price = (
                parseFloat(basketStore.basket.total.price) + parseFloat(paymentMethod.transaction_fee.price)
            ).toString();
            basketStore.basket.total.price_excluding_vat = calculatePriceExcludingVat(
                vatPercentage,
                +basketStore.basket.total.price,
            ).toString();
            basketStore.basket.payment_method = paymentMethod;
            basketStore.basket.payment_method_id = paymentMethod.id;
        }
    }

    function presetDeliveryMethod() {
        // TODO Temporary fix for the preset delivery method: the interval has been added. This fix is linked to ticket DSN-6413.
        const interval = setInterval(() => {
            if (shippingMethods.value?.length) {
                clearInterval(interval);
                if (!basketStore.basket?.shipping_method && !basketStore.basket?.pickup_point) {
                    updateShippingMethodLocal(shippingMethods.value[0]);
                }
            }
        }, 500);
    }

    function presetPaymentMethod() {
        if (paymentMethods.value?.length && !basketStore.basket?.payment_method) {
            updatePaymentMethodLocal(paymentMethods.value[0]);
        }
    }

    function continueWithShippingMethod() {
        isNextStepLoading.value = true;

        CheckoutCustomizationEventBus.emit("beforeDeliveryStepContinue", new BeforeDeliveryStepContinue());

        basketStore
            .updateDeliveryMethod()
            .then((response: any) => {
                isNextStepLoading.value = false;
                navigateToStep(redirectURLDelivery.value);
            })
            .catch((error) => {
                isNextStepLoading.value = false;
                console.log(error);
            });
    }

    function continueWithPickupPoint() {
        isNextStepLoading.value = true;

        basketStore
            .updateDeliveryMethod()
            .then((response: any) => {
                isNextStepLoading.value = false;
                navigateToStep(redirectURLDelivery.value);
            })
            .catch((error) => {
                isNextStepLoading.value = false;
                console.log(error);
            });
    }

    function setFormValidationMessages(errors: []) {
        formValidationMessages.value = errors;
    }

    async function doCheckout() {
        isNextStepLoading.value = true;

        try {
            await basketStore.updatePaymentMethod();
        } catch (error: any) {
            isNextStepLoading.value = false;

            if (error.response?.data?.errors) {
                formValidationMessages.value = error.response.data.errors;
            }

            throw error;
        }

        // Was before in confirm page
        let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
        let paymentFee = basketStore.basket.payment_method?.transaction_fee?.price ?? 0;

        trackGACheckoutSummary(
            mappedGAProducts,
            basketStore.basket.customer?.telephone,
            basketStore.basket.customer?.email,
            basketStore.basket.shipping_method?.tier?.name,
            basketStore.basket.payment_method?.name,
            +paymentFee,
        );

        // If the checkout call was not yet performed
        if (!cachedCreateOrderResponse.value) {
            try {
                // Create an order by checking out the Basket
                let response = await apiService.post("v2/basket/checkout");

                // Cache the Order resource returned by the API
                cachedCreateOrderResponse.value = response.data.data;
            } catch (error: any) {
                // If the Basket checkout call failed, emit an event
                let event = new AfterCheckoutError(error.response);
                CheckoutCustomizationEventBus.emit("afterCheckoutError", event);

                // Update the state of the next step button
                isNextStepLoading.value = false;

                // Exit this method
                throw error;
            }
        }

        // Build up the payload for the 'pay' API call
        let payOrderPayload: {
            order_id: number;
            method_id: number;
            redirect_url: string;
            cancel_url: string;
            issuer_id?: string;
        } = {
            order_id: cachedCreateOrderResponse.value.id,
            method_id: basketStore.basket.payment_method_id,
            redirect_url: window.location.origin + "/checkout/success",
            cancel_url: window.location.href,
        };

        // (optionally) set the issuer_id on the payload for the 'pay' API call
        if (basketStore.basket.payment_method.issuer) {
            payOrderPayload.issuer_id = basketStore.basket.payment_method.issuer.id;
        }

        // Initialize the payment flow for the current order
        apiService
            .post("v2/order/pay", payOrderPayload)
            .then((response) => {
                // Allow top-level project to overwrite the 'pay' behaviour
                let event = new AfterOrderPay(response.data);
                CheckoutCustomizationEventBus.emit("afterOrderPay", event);

                // If the top-level requests preventing of default behaviour, don't continue the default
                // redirect behaviour here.
                if (event.requestPreventDefaultBehaviour) {
                    // If a custom redirect url was defined, use it
                    if (event.customRedirectUrl) {
                        window.location.href = event.customRedirectUrl;

                        // Enable continue button(s) again after 2 seconds, in case the user cancels the navigation to the
                        // next page
                        setTimeout(() => {
                            isNextStepLoading.value = false;
                        }, 3000);

                        return;
                    } else {
                        // Else, don't execute any more logic in this method
                        return;
                    }
                }

                // Navigate to the payment url provided by the API response. Most often, this is an url to a 3rd-party
                // payment provider.
                window.location.href = response.data.payment_url || response.data.data.payment_url;

                // Enable continue button(s) again after 2 seconds, in case the user cancels the navigation to the
                // next page
                setTimeout(() => {
                    isNextStepLoading.value = false;
                }, 3000);
            })
            .catch((error) => {
                console.log(error);

                // If the 'pay' API call failed, emit an event
                let event = new AfterCheckoutError(error.response);
                CheckoutCustomizationEventBus.emit("afterCheckoutError", event);

                throw error;
            })
            .finally(() => {
                // Update the state of the next step button
                isNextStepLoading.value = false;
            });
    }

    function onSuccessPageLoaded(transactionId: string = null) {
        let mappedGAProducts = mapBasketEntriesToGAProductsData(basketStore.basket.entries);
        let shippingTierName = basketStore.basket.shipping_method?.tier?.name;
        let paymentMethodName = basketStore.basket.payment_method?.name;
        let shippingFee =
            basketStore.basket.shipping_method?.price?.price ?? basketStore.basket.pickup_point?.price?.price ?? 0;
        let paymentFee = basketStore.basket.payment_method?.transaction_fee?.price ?? 0;

        trackGAPurchase(
            mappedGAProducts,
            transactionId,
            basketStore.basket.customer.telephone,
            basketStore.basket.customer.email,
            shippingTierName,
            paymentMethodName,
            +shippingFee,
            +paymentFee,
            basketStore.basket.discount_code?.code ?? "",
        );

        basketStore.clearBasket();
    }

    function doRequestContinueToNextStep() {
        formValidationMessages.value = [];

        if (currentStep.value === CheckoutSteps.CUSTOMERINFO) {
            // This is picked up by ClientForm.vue
            requestContinueToNextStep.value = true;

            CheckoutStoreEventBus.emit("afterNextStep");
            return;
        }

        if (currentStep.value === CheckoutSteps.DELIVERY) {
            if (basketStore.basket?.shipping_method) {
                continueWithShippingMethod();
            } else if (basketStore.basket?.pickup_point) {
                continueWithPickupPoint();
            }

            CheckoutStoreEventBus.emit("afterNextStep");

            return;
        }

        if (currentStep.value === CheckoutSteps.PAYMENT && basketStore.basket?.payment_method) {
            if (!!window.checkout_must_confirm_terms_and_conditions && !areTermsAccepted.value) {
                return;
            }

            doCheckout();

            CheckoutStoreEventBus.emit("afterNextStep");
            return;
        }
    }

    return {
        minOrderPrice,
        shippingMethods,
        pickupPoints,
        paymentMethods,
        paymentMethodHasError,
        redirectURLDelivery,
        areTermsAccepted,
        hasValidNoMinimumOrderValueCookie,
        minOrderAmountRuleIsMet,
        pickupPointIsAllowed,
        selectedShippingMethod,
        clientFormIsValid,
        customOverwriteNextStepStateToDisabled,
        presetPaymentMethod,
        presetDeliveryMethod,
        selectedPickupPoint,
        nextStepState,
        paymentMethodMinMaxOrderValueRuleMet,
        isNextStepLoading,
        formValidationMessages,
        registerOrUpdateCustomer,
        onSuccessPageLoaded,
        doRequestContinueToNextStep,
        requestContinueToNextStep,
        updateShippingMethodLocal,
        updatePickupPointLocal,
        updatePaymentMethodLocal,
        isPickupListOpen,
        getPickupPrice,
        currentStep,
        navigateToStep,
        setFormValidationMessages,
    };
});
